// 현장 사진 원본 경로 — 세션 전용 라우트(`/app/projects/[id]/photos/[photoId]`)라 바이트를 복사하지 않는다.

import type { PortfolioImageStage } from "./types";

export const projectPhotoPath = (projectId: string, photoId: string): string =>
  `/app/projects/${encodeURIComponent(projectId)}/photos/${encodeURIComponent(photoId)}`;

export const isProjectPhotoPath = (url: string): boolean => /^\/app\/projects\/[^/\s?#]+\/photos\/[^/\s?#]+$/.test(url);

export const PORTFOLIO_PHOTO_NO_PROJECT_MESSAGE = "현장 사진은 현장에서 만든 시공사례에만 넣을 수 있습니다";
export const PORTFOLIO_PHOTO_UNKNOWN_MESSAGE = "현장 사진을 찾을 수 없습니다 — 화면을 새로 고쳐 주세요";
export const droppedPhotosNotice = (n: number): string => `지워진 현장 사진 ${n}장을 뺐습니다`;

type ImageIn = { url: string; caption: string; photo_id?: string; stage?: PortfolioImageStage; space?: string };

// 사진 태그(전/후·공간)는 장을 다시 만들 때도 그대로 싣는다.
const tagsOf = (image: ImageIn): Pick<ImageIn, "stage" | "space"> => ({
  ...(image.stage !== undefined ? { stage: image.stage } : {}),
  ...(image.space !== undefined ? { space: image.space } : {}),
});

export type PhotoImagesResult =
  | { ok: true; images: ImageIn[]; cover_url: string; dropped_photo_ids: string[] }
  | { ok: false; message: string };

// 대표가 장 밖의 현장 사진이면(지워졌거나 뺐으면) 남은 첫 장이 대표가 된다.
const coverAmong = (coverUrl: string, images: readonly ImageIn[]): string =>
  isProjectPhotoPath(coverUrl) && !images.some((i) => i.url === coverUrl) ? (images[0]?.url ?? "") : coverUrl;

// 폼이 보낸 현장 사진 장 검사 — 살아 있으면 정본 경로로, 원래 이 사례에 있던 지워진 사진이면 빼고, 그 밖(다른 현장·조직)은 거부한다.
export const resolvePhotoImages = (
  images: readonly ImageIn[],
  coverUrl: string,
  projectId: string | null,
  livePhotoIds: ReadonlySet<string>,
  storedPhotoIds: ReadonlySet<string> = new Set()
): PhotoImagesResult => {
  const out: ImageIn[] = [];
  const dropped: string[] = [];
  for (const [index, image] of images.entries()) {
    if (image.photo_id === undefined) {
      out.push({ url: image.url, caption: image.caption, ...tagsOf(image) });
      continue;
    }
    if (projectId !== null && livePhotoIds.has(image.photo_id)) {
      out.push({ url: projectPhotoPath(projectId, image.photo_id), caption: image.caption, photo_id: image.photo_id, ...tagsOf(image) });
      continue;
    }
    if (storedPhotoIds.has(image.photo_id)) {
      dropped.push(image.photo_id);
      continue;
    }
    if (projectId === null) return { ok: false, message: PORTFOLIO_PHOTO_NO_PROJECT_MESSAGE };
    return { ok: false, message: `${index + 1}번째 사진: ${PORTFOLIO_PHOTO_UNKNOWN_MESSAGE}` };
  }
  return { ok: true, images: out, cover_url: coverAmong(coverUrl, out), dropped_photo_ids: dropped };
};

// 목록·카드용 — 지워진 현장 사진 장을 건너뛰고 대표를 다음 장으로 넘긴다(저장된 행은 그대로).
export const withoutMissingPhotos = <T extends ImageIn>(
  portfolio: { images: readonly T[]; cover_url: string },
  livePhotoIds: ReadonlySet<string>
): { images: T[]; cover_url: string } => {
  const images = portfolio.images.filter((i) => i.photo_id === undefined || livePhotoIds.has(i.photo_id));
  return { images, cover_url: coverAmong(portfolio.cover_url, images) };
};
